import React from 'react';
import { motion } from 'framer-motion';
import { createBrowserRouter, Navigate, RouteObject, useLocation } from 'react-router-dom';
import App from '@/App';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { lazyWithRetry, type PreloadableLazy } from '@/lib/lazyWithRetry';
import { installViewTransitions } from '@/lib/viewTransitions';
import HomePage from '@/pages/HomePage';
import NotFoundPage from '@/pages/NotFoundPage';
import MerchBoundary from '@/components/merch/MerchBoundary';

// Every route element goes through lazyWithRetry so a stale tab recovers from a
// missing chunk instead of throwing into the error boundary. The homepage stays
// eager because it is the first paint, and NotFoundPage stays eager because it
// is what the error boundary falls back to.
const ArtistPage = lazyWithRetry(() => import('@/pages/ArtistPage'));
const RedirectPage = lazyWithRetry(() => import('@/pages/RedirectPage'));
const AlbumsPage = lazyWithRetry(() => import('@/pages/artist/AlbumsPage'));
const AlbumDetailPage = lazyWithRetry(() => import('@/pages/artist/AlbumDetailPage'));
const BioPage = lazyWithRetry(() => import('@/pages/artist/BioPage'));
const ReleasePage = lazyWithRetry(() => import('@/pages/artist/ReleasePage'));
const SongsPage = lazyWithRetry(() => import('@/pages/artist/SongsPage'));
const TrackPage = lazyWithRetry(() => import('@/pages/artist/TrackPage'));
const VideosPage = lazyWithRetry(() => import('@/pages/artist/VideosPage'));
const NewsletterPage = lazyWithRetry(() => import('@/pages/NewsletterPage'));
const MerchPage = lazyWithRetry(() => import('@/pages/MerchPage'));
const GamesPage = lazyWithRetry(() => import('@/pages/GamesPage'));
const GamePage = lazyWithRetry(() => import('@/pages/GamePage'));
const EndlessPage = lazyWithRetry(() => import('@/pages/EndlessPage'));
const MerchItemPage = lazyWithRetry(() => import('@/pages/MerchItemPage'));
const MerchThanksPage = lazyWithRetry(() => import('@/pages/MerchThanksPage'));
const MerchOrderPage = lazyWithRetry(() => import('@/pages/MerchOrderPage'));
const AdminPage = lazyWithRetry(() => import('@/pages/admin/AdminPage'));
const MembersPage = lazyWithRetry(() => import('@/pages/members/MembersPage'));

function LegacyMembersRedirect() {
  const location = useLocation();
  return <Navigate to={`${location.pathname.replace(/^\/members/, '/branches')}${location.search}${location.hash}`} replace />;
}

const pageVariants = {
  initial: { opacity: 0 },
  in: { opacity: 1 },
  out: { opacity: 0 },
};

const pageTransition = {
  type: 'tween',
  duration: 0.4,
  ease: 'easeInOut',
};

// Keyed by pathname so moving between two pages of the same route (one song
// page to another) mounts a fresh page instead of reusing the old one's state.
const PageWrapper = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      <React.Suspense fallback={null} key={pathname}>{children}</React.Suspense>
    </motion.div>
  );
};

// `handle.preload` lets the view transition controller fetch a route's chunk
// on hover, focus or touch, so the page renders on the first commit of the
// navigation instead of showing an empty Suspense fallback.
const preloadOf = (...components: Array<PreloadableLazy<any>>) => ({
  preload: () => Promise.all(components.map((component) => component.preload())),
});

const routes: RouteObject[] = [
  {
    element: <App />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/',
        element: <PageWrapper><HomePage /></PageWrapper>,
              },
      {
        path: '/bio',
        element: <PageWrapper><BioPage /></PageWrapper>,
        handle: preloadOf(BioPage),
      },
      {
        path: '/songs',
        element: <PageWrapper><SongsPage /></PageWrapper>,
        handle: preloadOf(SongsPage),
      },
      {
        path: '/songs/:trackName',
        element: <PageWrapper><TrackPage /></PageWrapper>,
        handle: preloadOf(TrackPage),
      },
      {
        path: '/albums',
        element: <PageWrapper><AlbumsPage /></PageWrapper>,
        handle: preloadOf(AlbumsPage),
      },
      {
        path: '/albums/:albumName',
        element: <PageWrapper><AlbumDetailPage /></PageWrapper>,
        handle: preloadOf(AlbumDetailPage),
      },
      {
        path: '/videos',
        element: <PageWrapper><VideosPage /></PageWrapper>,
        handle: preloadOf(VideosPage),
      },
      {
        path: '/newsletter',
        element: <Navigate to="/" replace />,
      },
      {
        path: '/newsletter/confirm',
        element: (
          <React.Suspense fallback={null}>
            <NewsletterPage />
          </React.Suspense>
        ),
      },
      {
        path: '/newsletter/unsubscribe',
        element: (
          <React.Suspense fallback={null}>
            <NewsletterPage />
          </React.Suspense>
        ),
      },
      {
        path: '/merch',
        element: <PageWrapper><MerchBoundary><MerchPage /></MerchBoundary></PageWrapper>,
        handle: preloadOf(MerchPage),
      },
      {
        path: '/merch/thanks',
        element: <PageWrapper><MerchBoundary><MerchThanksPage /></MerchBoundary></PageWrapper>,
        handle: preloadOf(MerchThanksPage),
      },
      {
        path: '/merch/orders/:token',
        element: <PageWrapper><MerchBoundary><MerchOrderPage /></MerchBoundary></PageWrapper>,
        handle: preloadOf(MerchOrderPage),
      },
      {
        path: '/merch/:designSlug',
        element: <PageWrapper><MerchBoundary><MerchItemPage /></MerchBoundary></PageWrapper>,
        handle: preloadOf(MerchItemPage),
      },
      {
        path: '/games',
        element: <PageWrapper><GamesPage /></PageWrapper>,
        handle: preloadOf(GamesPage),
      },
      {
        path: '/games/endless',
        element: <PageWrapper><EndlessPage /></PageWrapper>,
        handle: preloadOf(EndlessPage),
      },
      {
        path: '/games/:songSlug',
        element: <PageWrapper><GamePage /></PageWrapper>,
        handle: preloadOf(GamePage),
      },
      {
        path: '/games/:songSlug/:gameKind',
        element: <PageWrapper><GamePage /></PageWrapper>,
      },
      {
        path: '/admin/*',
        element: (
          <React.Suspense fallback={null}>
            <AdminPage />
          </React.Suspense>
        ),
      },
      {
        path: '/branches/*',
        element: (
          <React.Suspense fallback={null}>
            <MembersPage />
          </React.Suspense>
        ),
      },
      {
        path: '/members/*',
        element: <LegacyMembersRedirect />,
      },
      {
        path: '/redirect/:platform/:kind/:id',
        element: <PageWrapper><RedirectPage /></PageWrapper>,
        handle: preloadOf(RedirectPage),
      },
      {
        path: '/redirect/:kind/:id',
        element: <PageWrapper><RedirectPage /></PageWrapper>,
        handle: preloadOf(RedirectPage),
      },
      {
        path: '/r/:platform/:kind/:id',
        element: <PageWrapper><RedirectPage /></PageWrapper>,
        handle: preloadOf(RedirectPage),
      },
      {
        path: '/r/:kind/:id',
        element: <PageWrapper><RedirectPage /></PageWrapper>,
        handle: preloadOf(RedirectPage),
      },
      {
        path: '/:artist',
        element: <PageWrapper><ArtistPage /></PageWrapper>,
        handle: preloadOf(ArtistPage),
      },
      {
        path: '/:artist/bio',
        element: <PageWrapper><BioPage /></PageWrapper>,
        handle: preloadOf(BioPage),
      },
      {
        path: '/:artist/songs',
        element: <PageWrapper><SongsPage /></PageWrapper>,
        handle: preloadOf(SongsPage),
      },
      {
        path: '/:artist/songs/:trackName',
        element: <PageWrapper><TrackPage /></PageWrapper>,
        handle: preloadOf(TrackPage),
      },
      {
        path: '/:artist/albums',
        element: <PageWrapper><AlbumsPage /></PageWrapper>,
        handle: preloadOf(AlbumsPage),
      },
      {
        path: '/:artist/albums/:albumName',
        element: <PageWrapper><AlbumDetailPage /></PageWrapper>,
        handle: preloadOf(AlbumDetailPage),
      },
      {
        path: '/:artist/videos',
        element: <PageWrapper><VideosPage /></PageWrapper>,
        handle: preloadOf(VideosPage),
      },
      {
        path: '/:artist/:release',
        element: <PageWrapper><ReleasePage /></PageWrapper>,
        handle: preloadOf(ReleasePage),
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];

export const router = createBrowserRouter(routes);

installViewTransitions(router);
